import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/AppLayout";
import { StatusBadge, TurnaroundBadge } from "@/components/Badges";
import { api } from "@/lib/api";
import { RateJob } from "@/components/Rating";
import { REVIEW_STATUS } from "@/lib/constants";

const FLOW = ["submitted", "in_review", "certified"];

function Timeline({ status }) {
  const idx = status === "changes_suggested" ? 1 : FLOW.indexOf(status);
  return (
    <div className="flex items-center gap-2 mt-4">
      {FLOW.map((s, i) => (
        <div key={s} className="flex items-center gap-2 flex-1">
          <span className={`h-2.5 w-2.5 rounded-full ${i <= idx ? "bg-emerald-500" : "bg-slate-200"}`} />
          <span className={`text-xs ${i <= idx ? "text-slate-700" : "text-slate-400"}`}>{REVIEW_STATUS[s].label}</span>
          {i < FLOW.length - 1 && <span className={`h-px flex-1 ${i < idx ? "bg-emerald-400" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/reviews").then((r) => setItems(r.data)); }, []);
  const view = async (rid) => setOpen((await api.get(`/reviews/${rid}`)).data);
  return (
    <div>
      <PageHeader eyebrow="Certified human review" title="Final compliance reviews">
        Proposals you've sent to the Peer Verify network of certified grant editors. Send a new one from any proposal page.
      </PageHeader>
      {!items ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="reviews-empty">
          <BadgeCheck className="h-8 w-8 mx-auto text-amber-500" />
          <p className="mt-4 text-slate-500">No reviews yet. Open a proposal and click "Send for human review".</p>
          <Link to="/dashboard/proposals" className="text-emerald-700 font-semibold text-sm mt-3 inline-block" data-testid="reviews-go-to-proposals-link">Go to proposals →</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <button key={r.review_id} onClick={() => view(r.review_id)} data-testid={`review-row-${r.review_id}`} className="card-lift w-full text-left bg-white border border-slate-200 border-l-4 border-l-amber-500 rounded-lg p-6">
              <div className="flex justify-between gap-4 flex-wrap">
                <div><h3 className="font-semibold text-slate-900">{r.proposal_title}</h3><p className="text-xs text-slate-500 mt-1">Submitted {new Date(r.submitted_at || r.created_at).toLocaleDateString()}{r.editor ? ` · Editor: ${r.editor}` : ""}</p></div>
                <span className="flex items-center gap-2"><TurnaroundBadge r={r} testid={`review-turnaround-${r.review_id}`} /><StatusBadge status={r.status} map={REVIEW_STATUS} testid={`review-status-${r.review_id}`} /></span>
              </div>
              <Timeline status={r.status} />
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="review-detail-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{open?.proposal_title}</DialogTitle></DialogHeader>
          {open && <>
            <StatusBadge status={open.status} map={REVIEW_STATUS} />
            <div className="mt-4"><h4 className="font-mono text-xs uppercase tracking-[0.18em] text-amber-700 mb-2">Editor notes</h4><p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="review-editor-notes">{open.editor_notes || "Your editor hasn't added notes yet. Reviews are typically returned within 48 hours."}</p></div>
            {open.status === "certified" && <RateJob path={`/reviews/${open.review_id}/rating`} initial={open.my_rating} who={open.assigned_name || open.editor} testid="review-rating" />}
            {open.revised_content && <div className="mt-6"><h4 className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-700 mb-2">Revised proposal</h4><div className="prose-gv border border-slate-200 rounded-lg p-6"><ReactMarkdown remarkPlugins={[remarkGfm]}>{open.revised_content}</ReactMarkdown></div></div>}
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
